/**
 * Per-school actions menu opened from the Schools tab ("School settings"):
 * plan, invite, seats, seat requests, mentors, Stripe sync, activation,
 * usage reset, and "open in Settings".
 */
import React from 'react';
import { ActivityIndicator, Modal, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { ADMIN_COLORS } from '@/components/admin/AdminUI';
import { useTranslation } from '../../../../hooks/useTranslation';
import { ROUTES } from '../../../../navigation/routes';
import { getBillingSeed } from '../platformAdminFormat';
import type { School } from '../platformAdminTypes';
import type { SchoolActions } from '../hooks/useSchoolActions';

interface SchoolActionsModalProps {
  visible: boolean;
  school: School;
  schoolActions: SchoolActions;
  onOpenInSettings: (school: School) => void;
}

export default function SchoolActionsModal({
  visible,
  school,
  schoolActions,
  onOpenInSettings: focusSchoolInSettings,
}: SchoolActionsModalProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const {
    resettingSchoolUsageId,
    closeSchoolSettingsMenu,
    executeSchoolAction,
    setShowPlanModal,
    setShowBillModal,
    setShowSeatModal,
    setShowPlanChangeRequests,
    syncStripe,
    toggleSubscription,
    handleResetSchoolUsage,
  } = schoolActions;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={closeSchoolSettingsMenu}
    >
      <View className="flex-1 bg-black/50 items-center justify-center p-4">
        <TouchableOpacity
          className="absolute inset-0"
          activeOpacity={1}
          onPress={closeSchoolSettingsMenu}
        />
        <Card style={{ width: '100%', maxWidth: 480 }}>
          <CardContent className="p-6">
            <View className="flex-row items-start justify-between mb-4">
              <View className="flex-1 pr-4">
                <Text className="text-base font-semibold text-foreground">
                  {t('platformAdmin.schoolActionsTitle')}
                </Text>
                <Text className="text-sm text-muted-foreground mt-1">
                  {t('platformAdmin.schoolActionsSubtitle', {
                    name: school.name,
                  })}
                </Text>
              </View>
              <TouchableOpacity onPress={closeSchoolSettingsMenu} className="p-1">
                <MaterialIcons name="close" size={22} color="#9ca3af" />
              </TouchableOpacity>
            </View>

            <View className="gap-3">
              <TouchableOpacity
                className="flex-row items-start gap-3 rounded-xl border border-gray-200 bg-white/70 px-4 py-3"
                activeOpacity={0.9}
                onPress={() =>
                  executeSchoolAction(() =>
                    setShowPlanModal({
                      visible: true,
                      schoolId: school.id,
                      seed: getBillingSeed(school),
                    })
                  )
                }
              >
                <View className="w-10 h-10 rounded-full items-center justify-center bg-blue-100">
                  <MaterialIcons name="price-change" size={22} color={ADMIN_COLORS.primary} />
                </View>
                <View className="flex-1">
                  <Text className="text-sm font-semibold text-foreground">
                    {t('platformAdmin.editPlan')}
                  </Text>
                  <Text className="text-xs text-muted-foreground mt-1">
                    {t('platformAdmin.schoolActionEditPlanDesc')}
                  </Text>
                </View>
                <MaterialIcons name="chevron-right" size={18} color="#9ca3af" />
              </TouchableOpacity>

              <TouchableOpacity
                className="flex-row items-start gap-3 rounded-xl border border-gray-200 bg-white/70 px-4 py-3"
                activeOpacity={0.9}
                onPress={() =>
                  executeSchoolAction(() =>
                    setShowBillModal({
                      visible: true,
                      schoolId: school.id,
                      seed: getBillingSeed(school),
                    })
                  )
                }
              >
                <View className="w-10 h-10 rounded-full items-center justify-center bg-amber-100">
                  <MaterialIcons name="payments" size={22} color={ADMIN_COLORS.secondary} />
                </View>
                <View className="flex-1">
                  <Text className="text-sm font-semibold text-foreground">
                    {t('platformAdmin.billAndInvite')}
                  </Text>
                  <Text className="text-xs text-muted-foreground mt-1">
                    {t('platformAdmin.schoolActionBillInviteDesc')}
                  </Text>
                </View>
                <MaterialIcons name="chevron-right" size={18} color="#9ca3af" />
              </TouchableOpacity>

              <TouchableOpacity
                className="flex-row items-start gap-3 rounded-xl border border-gray-200 bg-white/70 px-4 py-3"
                activeOpacity={0.9}
                onPress={() =>
                  executeSchoolAction(() =>
                    setShowSeatModal({
                      visible: true,
                      schoolId: school.id,
                      current: school.max_students,
                    })
                  )
                }
              >
                <View className="w-10 h-10 rounded-full items-center justify-center bg-slate-100">
                  <MaterialIcons name="stacked-line-chart" size={22} color={ADMIN_COLORS.primary} />
                </View>
                <View className="flex-1">
                  <Text className="text-sm font-semibold text-foreground">
                    {t('platformAdmin.updateSeats')}
                  </Text>
                  <Text className="text-xs text-muted-foreground mt-1">
                    {t('platformAdmin.schoolActionUpdateSeatsDesc')}
                  </Text>
                </View>
                <MaterialIcons name="chevron-right" size={18} color="#9ca3af" />
              </TouchableOpacity>

              <TouchableOpacity
                className="flex-row items-start gap-3 rounded-xl border border-gray-200 bg-white/70 px-4 py-3"
                activeOpacity={0.9}
                onPress={() =>
                  setShowPlanChangeRequests({
                    visible: true,
                    schoolId: school.id,
                  })
                }
              >
                <View className="w-10 h-10 rounded-full items-center justify-center bg-emerald-100">
                  <MaterialIcons name="playlist-add-check" size={22} color={ADMIN_COLORS.success} />
                </View>
                <View className="flex-1">
                  <Text className="text-sm font-semibold text-foreground">
                    {t('platformAdmin.seatRequests')}
                  </Text>
                  <Text className="text-xs text-muted-foreground mt-1">
                    {t('platformAdmin.schoolActionSeatRequestsDesc')}
                  </Text>
                </View>
                <MaterialIcons name="chevron-right" size={18} color="#9ca3af" />
              </TouchableOpacity>

              <TouchableOpacity
                className="flex-row items-start gap-3 rounded-xl border border-gray-200 bg-white/70 px-4 py-3"
                activeOpacity={0.9}
                onPress={() =>
                  executeSchoolAction(() =>
                    router.push({
                      pathname: ROUTES.TABS_SCHOOL_MANAGEMENT,
                      params: { schoolId: String(school.id), tab: 'mentors' },
                    })
                  )
                }
              >
                <View className="w-10 h-10 rounded-full items-center justify-center bg-purple-100">
                  <MaterialIcons name="school" size={22} color="#7C3AED" />
                </View>
                <View className="flex-1">
                  <Text className="text-sm font-semibold text-foreground">
                    {t('platformAdmin.manageMentors')}
                  </Text>
                  <Text className="text-xs text-muted-foreground mt-1">
                    {t('platformAdmin.schoolActionManageMentorsDesc')}
                  </Text>
                </View>
                <MaterialIcons name="chevron-right" size={18} color="#9ca3af" />
              </TouchableOpacity>

              <TouchableOpacity
                className="flex-row items-start gap-3 rounded-xl border border-gray-200 bg-white/70 px-4 py-3"
                activeOpacity={0.9}
                onPress={() =>
                  executeSchoolAction(() => syncStripe(school))
                }
              >
                <View className="w-10 h-10 rounded-full items-center justify-center bg-indigo-100">
                  <MaterialIcons name="sync" size={22} color={ADMIN_COLORS.primary} />
                </View>
                <View className="flex-1">
                  <Text className="text-sm font-semibold text-foreground">
                    {t('platformAdmin.syncStripe')}
                  </Text>
                  <Text className="text-xs text-muted-foreground mt-1">
                    {t('platformAdmin.schoolActionSyncStripeDesc')}
                  </Text>
                </View>
                <MaterialIcons name="chevron-right" size={18} color="#9ca3af" />
              </TouchableOpacity>

              <TouchableOpacity
                className="flex-row items-start gap-3 rounded-xl border border-gray-200 bg-white/70 px-4 py-3"
                activeOpacity={0.9}
                onPress={() =>
                  executeSchoolAction(() => toggleSubscription(school))
                }
              >
                <View
                  className={`w-10 h-10 rounded-full items-center justify-center ${school.subscription_status === 'active'
                    ? 'bg-red-100'
                    : 'bg-emerald-100'
                    }`}
                >
                  <MaterialIcons
                    name={
                      school.subscription_status === 'active'
                        ? 'toggle-off'
                        : 'toggle-on'
                    }
                    size={22}
                    color={
                      school.subscription_status === 'active'
                        ? ADMIN_COLORS.error
                        : ADMIN_COLORS.success
                    }
                  />
                </View>
                <View className="flex-1">
                  <Text className="text-sm font-semibold text-foreground">
                    {school.subscription_status === 'active'
                      ? t('platformAdmin.deactivateSchool')
                      : t('platformAdmin.activateSchool')}
                  </Text>
                  <Text className="text-xs text-muted-foreground mt-1">
                    {school.subscription_status === 'active'
                      ? t('platformAdmin.schoolActionDeactivateDesc')
                      : t('platformAdmin.schoolActionActivateDesc')}
                  </Text>
                </View>
                <MaterialIcons name="chevron-right" size={18} color="#9ca3af" />
              </TouchableOpacity>

              <TouchableOpacity
                className={`flex-row items-start gap-3 rounded-xl border px-4 py-3 ${resettingSchoolUsageId === school.id
                  ? 'border-red-100 bg-red-50/60 opacity-70'
                  : 'border-red-200 bg-red-50/60'
                  }`}
                activeOpacity={0.9}
                disabled={resettingSchoolUsageId === school.id}
                onPress={() => handleResetSchoolUsage(school)}
              >
                <View className="w-10 h-10 rounded-full items-center justify-center bg-red-100">
                  {resettingSchoolUsageId === school.id ? (
                    <ActivityIndicator size="small" color={ADMIN_COLORS.error} />
                  ) : (
                    <MaterialIcons name="restart-alt" size={22} color={ADMIN_COLORS.error} />
                  )}
                </View>
                <View className="flex-1">
                  <Text className="text-sm font-semibold text-red-700">
                    {resettingSchoolUsageId === school.id
                      ? t('platformAdmin.resettingSchoolUsage')
                      : t('platformAdmin.resetSchoolUsage')}
                  </Text>
                  <Text className="text-xs text-red-700/80 mt-1">
                    {t('platformAdmin.schoolActionResetUsageDesc')}
                  </Text>
                </View>
                <MaterialIcons name="chevron-right" size={18} color="#ef4444" />
              </TouchableOpacity>

              <TouchableOpacity
                className="flex-row items-start gap-3 rounded-xl border border-gray-200 bg-white/70 px-4 py-3"
                activeOpacity={0.9}
                onPress={() =>
                  executeSchoolAction(() => focusSchoolInSettings(school))
                }
              >
                <View className="w-10 h-10 rounded-full items-center justify-center bg-slate-100">
                  <MaterialIcons name="settings" size={22} color={ADMIN_COLORS.primary} />
                </View>
                <View className="flex-1">
                  <Text className="text-sm font-semibold text-foreground">
                    {t('platformAdmin.schoolActionOpenSettings')}
                  </Text>
                  <Text className="text-xs text-muted-foreground mt-1">
                    {t('platformAdmin.schoolActionOpenSettingsDesc')}
                  </Text>
                </View>
                <MaterialIcons name="chevron-right" size={18} color="#9ca3af" />
              </TouchableOpacity>
            </View>

            <Button
              variant="outline"
              onPress={closeSchoolSettingsMenu}
              className="mt-6"
            >
              <Text className="text-sm font-semibold text-foreground">
                {t('platformAdmin.schoolActionCancel')}
              </Text>
            </Button>
          </CardContent>
        </Card>
      </View>
    </Modal>
  );
}
